/**
 * Shapes shared by the dictionary, the game rules, and the UI.
 *
 * Two things are configurable, and both change the puzzle rather than the
 * decoration: how many words are in play at once, and how long they are.
 */

/** How many words are being solved simultaneously. */
export type BoardCount = 1 | 2 | 4;

export const BOARD_COUNTS: readonly BoardCount[] = [1, 2, 4];

/** What each board count is called, after the games that made each one famous. */
export const BOARD_LABEL: Record<BoardCount, string> = {
  1: 'Single',
  2: 'Double',
  4: 'Quad',
};

/** Letters per word. */
export type WordLength = 4 | 5 | 6;

export const WORD_LENGTHS: readonly WordLength[] = [4, 5, 6];

/**
 * Guesses allowed: one per extra board, on top of the six a single word gets.
 *
 * So 6, 7 and 9 — the counts Wordle, Dordle and Quordle settled on. The shape
 * of it is that every board still needs its own solving turn, but the
 * information from a guess is shared by all of them, so the boards after the
 * first are cheaper than the first one was.
 *
 * Word length deliberately does not move this. A four-letter word is not an
 * easier puzzle than a five — there are fewer letters to learn from, and the
 * candidates that survive a guess are more alike — so paying out fewer guesses
 * for it would punish the harder setting.
 */
export function guessLimit(boards: BoardCount): number {
  return boards + 5;
}

/** How a letter came back: the three colours, plus the tile nothing is known about. */
export type TileState = 'empty' | 'absent' | 'present' | 'correct';

/**
 * A key's colour on the shared keyboard, per board.
 *
 * `solved` is not something a letter can be — it is what the whole board
 * becomes, and it lands on the keyboard because a solved board must stop
 * colouring keys. Leaving its greens on would have the keyboard advertising
 * information about the one word that no longer needs any.
 *
 * It only ever replaces a colour, never `empty`: a letter the player has not
 * tried has no news on any board, and a solved one must not invent some. See
 * `tried` in `keyStates`.
 */
export type KeyState = TileState | 'solved';
